import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import './pages.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const fileIcon = (name = '') => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) return '🖼️';
    if (['mp4', 'mov', 'avi'].includes(ext)) return '🎥';
    return '📎';
};

const badgeClass = (status = '') => {
    const s = String(status).toLowerCase();
    if (s === 'verified' || s === 'linked') return 'pg-badge-green';
    if (s === 'rejected' || s === 'failed') return 'pg-badge-red';
    return 'pg-badge-amber';
};

const isImageUrl = (value = '') => {
    const raw = String(value || '').trim();
    if (!raw) return false;
    if (raw.startsWith('data:image/')) return true;
    const clean = raw.split('?')[0].split('#')[0].toLowerCase();
    return /\.(jpg|jpeg|png|webp)$/.test(clean);
};

const pickAadhaarImageUrl = (aadhaar = {}) => {
    const candidates = [
        aadhaar?.jpegUrl,
        aadhaar?.photoUrl,
        aadhaar?.aadhaar_jpeg_url,
        aadhaar?.photo
    ];
    return candidates.find((url) => isImageUrl(url)) || '';
};

const UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png';

// 'Bank Statement' is deliberately absent: confirm-upload deletes the user's
// existing bank statements for that type. 'Signed Agreement' is admin-only.
const UPLOAD_DOC_TYPES = [
    { value: 'Aadhaar Card', twoSided: true },
    { value: 'PAN Card', twoSided: false },
    { value: 'Voter ID', twoSided: true },
    { value: 'Driving Licence', twoSided: true },
    { value: 'Passport', twoSided: true },
    { value: 'Electricity Bill', twoSided: false },
    { value: 'Gas Bill', twoSided: false },
    { value: 'Water Bill', twoSided: false },
    { value: 'Rent Agreement', twoSided: false },
    { value: 'Other', twoSided: false }
];

const formatBytes = (bytes = 0) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function DocumentsPage() {
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [docs, setDocs] = useState([]);
    const [profile, setProfile] = useState(null);
    const [bureau, setBureau] = useState(null);
    const [viewType, setViewType] = useState('');
    const [activeDocId, setActiveDocId] = useState(null);
    const [excelData, setExcelData] = useState(null); // { sheets: [{name, rows: [[cells]]}] }
    const [excelLoading, setExcelLoading] = useState(false);
    const [excelSheet, setExcelSheet] = useState(0);
    const [docBlobUrl, setDocBlobUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [msgType, setMsgType] = useState('error'); // 'success' | 'error'

    // Upload modal: pick a document type, then attach front (and back) images.
    const [showUpload, setShowUpload] = useState(false);
    const [uploadDocType, setUploadDocType] = useState('');
    const [otherDocName, setOtherDocName] = useState('');
    const [frontFile, setFrontFile] = useState(null);
    const [backFile, setBackFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('');
    const [uploadError, setUploadError] = useState('');
    const frontInputRef = useRef(null);
    const backInputRef = useRef(null);

    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    const token = session?.token || '';
    const role = session?.role || 'user';

    const fetchAll = useCallback(async () => {
        if (!userId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setMsg('');
        try {
            const [docsRes, profileRes, bureauRes] = await Promise.all([
                fetch(`${API_URL}/api/documents/${userId}?requestingUserId=${userId}&requestingUserRole=${role}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                }),
                fetch(`${API_URL}/api/auth/profile/${userId}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                }),
                fetch(`${API_URL}/api/bureau/consent-status`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                })
            ]);

            const docsJson = await docsRes.json().catch(() => ({}));
            const profileJson = await profileRes.json().catch(() => ({}));
            const bureauJson = await bureauRes.json().catch(() => ({}));

            if (docsRes.ok && docsJson.success) {
                setDocs((docsJson.documents || []).map((x) => ({
                    id: x.id,
                    name: x.file_name || x.document_type || 'Document',
                    type: x.document_type || 'Document',
                    status: x.is_aa_statement
                        ? (x.aa_status === 'FETCHED' ? 'verified' : 'linked')
                        : (x.verification_status || 'pending'),
                    url: x.signed_url || '',
                    size: x.file_size ? `${(x.file_size / 1048576).toFixed(2)} MB` : '—',
                    uploadedAt: x.uploaded_at ? new Date(x.uploaded_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—',
                    is_aa_statement: x.is_aa_statement || false,
                    has_pdf: x.has_pdf || false
                })));
            }

            if (profileRes.ok && profileJson.success) {
                setProfile(profileJson);
            }

            if (bureauRes.ok && bureauJson.success) {
                setBureau(bureauJson);
            }
        } catch {
            setMsg('Unable to load documents right now.');
        } finally {
            setLoading(false);
        }
    }, [role, token, userId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const aadhaar = profile?.kycRecords?.aadhaar || null;
    const pan = profile?.kycRecords?.pan || null;
    const aadhaarImageUrl = pickAadhaarImageUrl(aadhaar || {});
    const selectedDoc = useMemo(
        () => docs.find((d) => d.id === activeDocId) || docs[0] || null,
        [activeDocId, docs]
    );

    const cards = [
        {
            key: 'uploaded',
            title: 'Uploaded Documents',
            subtitle: `${docs.length} file(s) available`,
            icon: '📂',
            badge: docs.length ? 'Available' : 'No files',
            badgeClassName: docs.length ? 'pg-badge-green' : 'pg-badge-amber'
        },
        {
            key: 'aadhaar',
            title: 'Aadhaar Details',
            subtitle: aadhaar?.name || 'Not available yet',
            icon: '🪪',
            badge: aadhaar ? 'Verified' : 'Pending',
            badgeClassName: aadhaar ? 'pg-badge-green' : 'pg-badge-amber'
        },
        {
            key: 'pan',
            title: 'PAN Details',
            subtitle: pan?.panNumber || profile?.user?.panNumber || 'Not available yet',
            icon: '🧾',
            badge: pan?.status || 'Pending',
            badgeClassName: badgeClass(pan?.status || 'pending')
        },
        {
            key: 'credit-report',
            title: 'Credit Report',
            subtitle: bureau?.hasReport ? `CRIF Score: ${bureau.creditScore || '--'}` : 'Not available yet',
            icon: '📊',
            badge: bureau?.hasReport ? 'Available' : 'Pending',
            badgeClassName: bureau?.hasReport ? 'pg-badge-green' : 'pg-badge-amber'
        }
    ];

    const openView = async (type) => {
        setViewType(type);
        setExcelData(null);
        setExcelSheet(0);
        setDocBlobUrl(null);
        if (type === 'uploaded') setActiveDocId(docs[0]?.id || null);
    };

    const selectedDocType = UPLOAD_DOC_TYPES.find((t) => t.value === uploadDocType) || null;
    const isOtherDocType = uploadDocType === 'Other';
    const backRequired = !!selectedDocType?.twoSided;
    const canSubmitUpload = !!selectedDocType && !!frontFile && (!backRequired || !!backFile) && !uploading
        && (!isOtherDocType || !!otherDocName.trim());

    const resetUploadModal = () => {
        setShowUpload(false);
        setUploadDocType('');
        setOtherDocName('');
        setFrontFile(null);
        setBackFile(null);
        setUploadStatus('');
        setUploadError('');
    };

    // Presign -> PUT to S3 -> confirm. Resolves with the created document row.
    const uploadOne = async (file, documentType) => {
        const presignRes = await fetch(`${API_URL}/api/documents/presigned-url`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                fileName: file.name,
                fileType: file.type || 'application/pdf',
                documentType,
                userId
            })
        });

        if (!presignRes.ok) {
            const err = await presignRes.json().catch(() => ({}));
            throw new Error(err.message || `Failed to initialize upload (${presignRes.status})`);
        }

        const { presignedUrl, s3Key } = await presignRes.json();

        await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', presignedUrl);
            xhr.setRequestHeader('Content-Type', file.type || 'application/pdf');
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error(`S3 Upload failed (Status ${xhr.status})`));
            };
            xhr.onerror = () => reject(new Error('S3 Upload failed (Network Error)'));
            xhr.send(file);
        });

        const confirmRes = await fetch(`${API_URL}/api/documents/confirm-upload`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                s3Key,
                originalName: file.name,
                documentType,
                fileSize: file.size,
                userId
            })
        });

        const json = await confirmRes.json();
        if (!confirmRes.ok || !json.success) throw new Error(json.message || 'Upload failed.');
        return json.document;
    };

    const handleUploadSubmit = async () => {
        if (!userId) {
            setUploadError('Login session missing. Please sign in again.');
            return;
        }
        if (!canSubmitUpload) return;

        const label = isOtherDocType ? otherDocName.trim() : selectedDocType.value;
        // Only suffix the side when a back is actually attached, so single-sided
        // docs land in the DB as plain "Electricity Bill", not "… - Front".
        const hasBack = !!backFile;
        let frontUploaded = false;

        setUploading(true);
        setUploadError('');

        try {
            setUploadStatus(hasBack ? 'Uploading front side…' : 'Uploading document…');
            const frontDoc = await uploadOne(frontFile, hasBack ? `${label} - Front` : label);
            frontUploaded = true;

            if (hasBack) {
                setUploadStatus('Uploading back side…');
                await uploadOne(backFile, `${label} - Back`);
            }

            setUploadStatus('Finalizing…');
            await fetchAll();

            resetUploadModal();
            if (frontDoc?.id) {
                setActiveDocId(frontDoc.id);
                setViewType('uploaded');
            }
            setMsgType('success');
            setMsg(`${label} uploaded successfully.`);
            setTimeout(() => setMsg(''), 4000);
        } catch (err) {
            console.error('Upload Error:', err);
            setUploadStatus('');
            setUploadError(
                frontUploaded
                    ? `Front side was saved, but the back side failed: ${err.message}. Please re-attach the back side.`
                    : (err.message || 'Upload failed. Please try again.')
            );
            if (frontUploaded) {
                setBackFile(null);
                await fetchAll();
            }
        } finally {
            setUploading(false);
        }
    };

    // Load Excel or PDF blob when an AA statement doc is selected
    useEffect(() => {
        const doc = docs.find(d => d.id === activeDocId);
        if (!doc?.is_aa_statement || !doc.url) return;
        setExcelLoading(true);
        setExcelData(null);
        setDocBlobUrl(null);
        let fullUrl = doc.url.startsWith('http') ? doc.url : `${API_URL}${doc.url}`;
        if (fullUrl.includes('?')) {
            fullUrl += '&format=csv';
        } else {
            fullUrl += '?format=csv';
        }
        fetch(fullUrl, { 
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'include'
        })
            .then(async r => {
                if (!r.ok) { setExcelLoading(false); return; }
                const contentType = r.headers.get('content-type') || '';
                const contentDisposition = r.headers.get('content-disposition') || '';
                const isPdfResponse = contentType.includes('pdf') || contentDisposition.includes('.pdf');
                
                if (!isPdfResponse) {
                    const buf = await r.arrayBuffer();
                    const wb = XLSX.read(buf, { type: 'array' });
                    const sheets = wb.SheetNames.map(name => {
                        const allRows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '' });
                        if (!allRows.length) return { name, rows: [] };
                        const header = allRows[0].map(h => String(h || '').toLowerCase().trim());
                        const employerName = String(profile?.user?.employer || '').toLowerCase().trim();

                        const filtered = allRows.slice(1).filter(row => {
                            const text = row.join(' ').toLowerCase();
                            
                            // 1. Match employer or salary keywords
                            let isMatch = false;
                            if (employerName && text.includes(employerName)) {
                                isMatch = true;
                            } else if (text.includes('salary') || text.includes('neft')) {
                                isMatch = true;
                            }
                            if (!isMatch) return false;

                            // 2. Ensure it's a credit (or at least NOT a debit)
                            let isDebit = false;
                            
                            const typeIdx = header.findIndex(h => h === 'type' || h === 'txn type' || h === 'dr/cr');
                            if (typeIdx !== -1) {
                                const val = String(row[typeIdx] || '').toLowerCase().trim();
                                if (val === 'dr' || val === 'debit') isDebit = true;
                            }

                            const debitIdx = header.findIndex(h => h.includes('debit') || h === 'dr' || h === 'withdrawal');
                            if (debitIdx !== -1) {
                                const val = String(row[debitIdx] || '').trim();
                                if (val && val !== '0' && val !== '-' && parseFloat(val) > 0) {
                                    isDebit = true;
                                }
                            }

                            return !isDebit;
                        });
                        return { name, rows: [header, ...filtered] };
                    });
                    setExcelData({ sheets });
                } else {
                    const blob = await r.blob();
                    const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                    setDocBlobUrl(URL.createObjectURL(pdfBlob));
                }
                setExcelLoading(false);
            })
            .catch(() => setExcelLoading(false));
    }, [activeDocId, docs, token, profile]);

    // Cleanup blob URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            if (docBlobUrl) URL.revokeObjectURL(docBlobUrl);
        };
    }, [docBlobUrl]);

    return (
        <div className="pg-root">
            <div className="pg-panel" style={{ marginBottom: '1rem' }}>
                <div className="pg-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <h2>Documents</h2>
                        <span className="pg-panel-badge">Redesigned Overview</span>
                    </div>
                    <div>
                        <button className="pg-btn pg-btn-primary" disabled={uploading} onClick={() => setShowUpload(true)}>
                            {uploading ? 'Uploading...' : 'Upload Document'}
                        </button>
                    </div>
                </div>
                <p style={{ margin: 0, color: '#64748b' }}>
                    View your uploaded files, Aadhaar details, and PAN details from one place.
                </p>
            </div>

            {msg && (
                <div className={`pg-alert ${msgType === 'success' ? 'pg-alert-success' : 'pg-alert-danger'}`}>
                    <div className="pg-alert-body"><p>{msg}</p></div>
                </div>
            )}

            {loading ? (
                <div className="pg-loading"><div className="pg-spinner" /><span>Loading documents…</span></div>
            ) : (
                <div className="pg-cards-grid">
                    {cards.map((card) => (
                        <div key={card.key} className="pg-card" style={{ minHeight: '210px' }}>
                            <div className="pg-card-accent" />
                            <div className="pg-card-header">
                                <div>
                                    <div style={{ fontSize: '1.6rem', marginBottom: '0.45rem' }}>{card.icon}</div>
                                    <h3 className="pg-card-title">{card.title}</h3>
                                    <p className="pg-card-sub">{card.subtitle}</p>
                                </div>
                                <span className={`pg-badge ${card.badgeClassName}`}>{card.badge}</span>
                            </div>
                            <button
                                className="pg-btn pg-btn-primary"
                                style={{ marginTop: 'auto', width: '100%', justifyContent: 'center' }}
                                onClick={() => openView(card.key)}
                            >
                                View
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {showUpload && (
                <div className="pg-modal-overlay" onClick={() => !uploading && resetUploadModal()}>
                    <div className="pg-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pg-modal-header">
                            <h2>Upload Document</h2>
                            <button className="pg-modal-close" disabled={uploading} onClick={resetUploadModal}>×</button>
                        </div>

                        <div className="pg-modal-body">
                            <div className="pg-upload-field">
                                <label htmlFor="pg-doc-type">Document Type</label>
                                <select
                                    id="pg-doc-type"
                                    value={uploadDocType}
                                    disabled={uploading}
                                    onChange={(e) => {
                                        setUploadDocType(e.target.value);
                                        setOtherDocName('');
                                        setFrontFile(null);
                                        setBackFile(null);
                                        setUploadError('');
                                    }}
                                >
                                    <option value="">-- Select a document --</option>
                                    {UPLOAD_DOC_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.value}</option>
                                    ))}
                                </select>
                            </div>

                            {isOtherDocType && (
                                <div className="pg-upload-field">
                                    <label htmlFor="pg-doc-type-other">Specify Document Type *</label>
                                    <input
                                        id="pg-doc-type-other"
                                        type="text"
                                        value={otherDocName}
                                        disabled={uploading}
                                        maxLength={60}
                                        placeholder="e.g. Ration Card"
                                        onChange={(e) => {
                                            setOtherDocName(e.target.value);
                                            setUploadError('');
                                        }}
                                    />
                                </div>
                            )}

                            {!selectedDocType ? (
                                <p className="pg-upload-hint">
                                    Choose a document type to attach its front and back images.
                                </p>
                            ) : (
                                <>
                                    <div className="pg-upload-grid">
                                        {[
                                            { side: 'Front', file: frontFile, setFile: setFrontFile, ref: frontInputRef, required: true },
                                            { side: 'Back', file: backFile, setFile: setBackFile, ref: backInputRef, required: backRequired }
                                        ].map(({ side, file, setFile, ref, required }) => (
                                            <div key={side}>
                                                <div className="pg-upload-slot-label">
                                                    {side} Side {required ? '*' : <span className="pg-upload-optional">(optional)</span>}
                                                </div>
                                                <input
                                                    type="file"
                                                    ref={ref}
                                                    accept={UPLOAD_ACCEPT}
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        setFile(e.target.files?.[0] || null);
                                                        setUploadError('');
                                                        e.target.value = '';
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className={`pg-upload-slot ${file ? 'is-filled' : ''}`}
                                                    disabled={uploading}
                                                    onClick={() => ref.current?.click()}
                                                >
                                                    <span className="pg-upload-slot-icon">{file ? fileIcon(file.name) : '⬆️'}</span>
                                                    {file ? (
                                                        <>
                                                            <span className="pg-upload-slot-name">{file.name}</span>
                                                            <span className="pg-upload-slot-meta">{formatBytes(file.size)} · Click to replace</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="pg-upload-slot-name">Choose file</span>
                                                            <span className="pg-upload-slot-meta">PDF, JPG or PNG</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    {!backRequired && (
                                        <p className="pg-upload-hint">
                                            {isOtherDocType ? 'This document' : selectedDocType.value} usually has nothing on the back — attach it only if needed.
                                        </p>
                                    )}
                                </>
                            )}

                            {uploadStatus && <p className="pg-upload-hint">{uploadStatus}</p>}
                            {uploadError && (
                                <div className="pg-alert pg-alert-danger" style={{ marginTop: '0.75rem' }}>
                                    <div className="pg-alert-body"><p>{uploadError}</p></div>
                                </div>
                            )}
                        </div>

                        <div className="pg-modal-footer">
                            <button className="pg-btn" disabled={uploading} onClick={resetUploadModal}>Cancel</button>
                            <button className="pg-btn pg-btn-primary" disabled={!canSubmitUpload} onClick={handleUploadSubmit}>
                                {uploading ? 'Uploading…' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!!viewType && (
                <div className="pg-modal-overlay" onClick={() => setViewType('')}>
                    <div className="pg-modal pg-modal-lg" style={{ maxWidth: '860px' }} onClick={(e) => e.stopPropagation()}>
                        <div className="pg-modal-header">
                            <h2>
                                {viewType === 'uploaded' && 'Uploaded Documents Preview'}
                                {viewType === 'aadhaar' && 'Aadhaar Details Preview'}
                                {viewType === 'pan' && 'PAN Details Preview'}
                                {viewType === 'credit-report' && 'CRIF Credit Report'}
                            </h2>
                            <button className="pg-modal-close" onClick={() => setViewType('')}>×</button>
                        </div>

                        <div className="pg-modal-body">
                            {viewType === 'uploaded' && (
                                <>
                                    {!docs.length ? (
                                        <div className="pg-empty">
                                            <span className="pg-empty-icon">📂</span>
                                            <h3>No uploaded documents</h3>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                            <div style={{ display: 'flex', overflowX: 'auto', gap: '0.5rem', paddingBottom: '0.5rem', WebkitOverflowScrolling: 'touch' }}>
                                                {docs.map(doc => {
                                                    const isActive = activeDocId === doc.id || (!activeDocId && selectedDoc?.id === doc.id);
                                                    return (
                                                        <button 
                                                            key={doc.id}
                                                            onClick={() => setActiveDocId(doc.id)}
                                                            style={{ 
                                                                flexShrink: 0, textAlign: 'left', padding: '0.6rem 0.8rem', borderRadius: '10px', border: '1px solid',
                                                                borderColor: isActive ? '#6366f1' : '#e2e8f0',
                                                                background: isActive ? '#eef2ff' : '#fff',
                                                                cursor: 'pointer', display: 'flex', flexDirection: 'column', minWidth: '180px', maxWidth: '220px'
                                                            }}
                                                        >
                                                            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{doc.type}</div>
                                                            <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{fileIcon(doc.name)} {doc.name}</div>
                                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.15rem' }}>{doc.uploadedAt}</div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden', background: '#f8fafc' }}>
                                                {selectedDoc ? (
                                                    <>
                                                        <div style={{ padding: '0.75rem 0.9rem', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                                <span className="pg-badge pg-badge-purple">{selectedDoc.type}</span>
                                                                <span style={{ fontWeight: 800, color: '#0f172a' }}>{selectedDoc.name}</span>
                                                            </div>
                                                            <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#64748b' }}>
                                                                {selectedDoc.uploadedAt} · {selectedDoc.size}
                                                            </div>
                                                        </div>
                                                        <div style={{ minHeight: '420px', overflow: 'auto' }}>
                                            {selectedDoc.is_aa_statement ? (
                                                excelLoading ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '420px', gap: '0.75rem', color: '#64748b' }}>
                                                        <div style={{ width: '28px', height: '28px', border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                                        Loading statement…
                                                    </div>
                                                ) : excelData ? (
                                                    <div style={{ height: '420px', overflow: 'auto', padding: '0.5rem' }}>
                                                        {excelData.sheets.length > 1 && (
                                                            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                                                {excelData.sheets.map((s, i) => (
                                                                    <button key={i} onClick={() => setExcelSheet(i)}
                                                                        style={{ padding: '0.25rem 0.75rem', borderRadius: '999px', border: '1px solid #e2e8f0', background: i === excelSheet ? '#6366f1' : '#fff', color: i === excelSheet ? '#fff' : '#334155', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' }}>
                                                                        {s.name}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', width: '100%', minWidth: '600px' }}>
                                                            <tbody>
                                                                {(excelData.sheets[excelSheet]?.rows || []).map((row, ri) => (
                                                                    <tr key={ri} style={{ background: ri === 0 ? '#f1f5f9' : ri % 2 === 0 ? '#f8fafc' : '#fff' }}>
                                                                        {row.map((cell, ci) => ri === 0 ? (
                                                                            <th key={ci} style={{ padding: '0.4rem 0.6rem', border: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 700, color: '#0f172a' }}>{cell}</th>
                                                                        ) : (
                                                                            <td key={ci} style={{ padding: '0.35rem 0.6rem', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', color: '#334155' }}>{cell}</td>
                                                                        ))}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : selectedDoc.name.toLowerCase().endsWith('.pdf') ? (
                                                    excelData ? null : docBlobUrl ? (
                                                        <iframe title="Document Preview" src={docBlobUrl} style={{ width: '100%', height: '420px', border: 'none' }} />
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '420px', gap: '0.75rem', color: '#64748b' }}>
                                                            <div style={{ width: '28px', height: '28px', border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                                            Loading PDF…
                                                        </div>
                                                    )
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '420px', gap: '1rem', color: '#475569', textAlign: 'center', padding: '2rem', background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)' }}>
                                                        <div style={{ fontSize: '3.5rem', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}>🏦</div>
                                                        <div style={{ fontWeight: 800, fontSize: '1.3rem', color: '#0f172a' }}>Statement Fetch in Progress</div>
                                                        <div style={{ fontSize: '0.95rem', maxWidth: '380px', color: '#475569', lineHeight: '1.5' }}>
                                                            Your bank account is securely linked via Account Aggregator. We are currently processing your raw financial data to generate a readable statement.
                                                        </div>
                                                        <div style={{ marginTop: '1rem', padding: '0.6rem 1.2rem', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '12px', color: '#047857', fontWeight: 700, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 2px 10px rgba(16, 185, 129, 0.1)' }}>
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                                            Status: {selectedDoc.status?.toUpperCase()}
                                                        </div>
                                                    </div>
                                                )
                                            ) : selectedDoc.name.toLowerCase().endsWith('.pdf') ? (
                                                <iframe title="Document Preview" src={selectedDoc.url} style={{ width: '100%', height: '420px', border: 'none' }} />
                                            ) : /\.(mp4|mov|avi)$/i.test(selectedDoc.name) ? (
                                                <video controls style={{ width: '100%', maxHeight: '420px' }}>
                                                    <source src={selectedDoc.url} type="video/mp4" />
                                                </video>
                                            ) : (
                                                <img src={selectedDoc.url} alt={selectedDoc.name} style={{ width: '100%', maxHeight: '420px', objectFit: 'contain', background: '#fff' }} />
                                            )}
                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {viewType === 'aadhaar' && (
                                <div style={{ display: 'grid', placeItems: 'center' }}>
                                    <div style={{
                                        width: 'min(100%, 760px)',
                                        borderRadius: '22px',
                                        border: '1px solid #d6d3d1',
                                        overflow: 'hidden',
                                        background: '#f5f5f4',
                                        boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
                                        padding: '1.2rem'
                                    }}>
                                        <div style={{
                                            borderRadius: '18px',
                                            background: '#fff',
                                            border: '1px solid #e7e5e4',
                                            boxShadow: '0 8px 20px rgba(15,23,42,0.1)',
                                            padding: '1.1rem'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.6rem' }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ height: '14px', borderRadius: '999px', background: 'linear-gradient(90deg,#f59e0b,#fde68a,#ffffff)', marginBottom: '0.5rem', maxWidth: '260px' }} />
                                                    <div style={{ height: '14px', borderRadius: '999px', background: 'linear-gradient(90deg,#16a34a,#86efac,#ffffff)', maxWidth: '260px' }} />
                                                </div>
                                                <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#991b1b', letterSpacing: '0.06em' }}>AADHAAR</div>
                                            </div>

                                            <div style={{ textAlign: 'center', fontSize: '2rem', fontWeight: 900, color: '#1f2937', letterSpacing: '0.04em', marginBottom: '0.8rem' }}>
                                                GOVERNMENT OF INDIA
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '1rem', alignItems: 'start' }}>
                                                <div style={{ width: '180px', height: '220px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#e5e7eb' }}>
                                                    {aadhaarImageUrl ? (
                                                        <img src={aadhaarImageUrl} alt="Aadhaar holder" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: '#6b7280', fontWeight: 700, fontSize: '0.85rem' }}>
                                                            Photo not available
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#111827', lineHeight: 1.1 }}>{aadhaar?.name || profile?.user?.name || '—'}</div>
                                                    <div style={{ marginTop: '0.4rem', fontSize: '2rem', color: '#111827' }}>{aadhaar?.gender || profile?.user?.gender || '—'}</div>
                                                    <div style={{ marginTop: '0.15rem', fontSize: '2rem', color: '#111827' }}>{aadhaar?.dob || (profile?.user?.dob ? new Date(profile.user.dob).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—')}</div>
                                                    <div style={{ marginTop: '0.6rem', fontSize: '3rem', fontWeight: 900, color: '#111827', letterSpacing: '0.08em', lineHeight: 1 }}>
                                                        {aadhaar?.uid || 'XXXX XXXX XXXX'}
                                                    </div>
                                                    <div style={{ marginTop: '0.8rem', fontSize: '1rem', color: '#475569', lineHeight: 1.4, borderTop: '1px solid #e2e8f0', paddingTop: '0.5rem' }}>
                                                        <strong>Address:</strong> {aadhaar?.address || profile?.user?.address || '—'}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: '0.9rem', borderTop: '6px solid #b91c1c' }} />
                                            <div style={{ textAlign: 'center', marginTop: '0.55rem', fontSize: '3rem', fontWeight: 900, color: '#111827', letterSpacing: '0.02em' }}>
                                                MY AADHAAR
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {viewType === 'pan' && (
                                <div className="pg-info-grid">
                                    <div className="pg-info-item"><span className="pg-info-label">PAN Number</span><span className="pg-info-value">{pan?.panNumber || profile?.user?.panNumber || '—'}</span></div>
                                    <div className="pg-info-item"><span className="pg-info-label">Full Name</span><span className="pg-info-value">{pan?.fullName || profile?.user?.name || '—'}</span></div>
                                    <div className="pg-info-item"><span className="pg-info-label">DOB</span><span className="pg-info-value">{pan?.dob || profile?.user?.dob || '—'}</span></div>
                                    <div className="pg-info-item"><span className="pg-info-label">Gender</span><span className="pg-info-value">{pan?.gender || profile?.user?.gender || '—'}</span></div>
                                    <div className="pg-info-item"><span className="pg-info-label">Status</span><span className="pg-info-value">{pan?.status || '—'}</span></div>
                                </div>
                            )}

                            {viewType === 'credit-report' && (
                                <div style={{ width: '100%', minHeight: '600px', display: 'flex', flexDirection: 'column' }}>
                                    {bureau?.crifPdfUrl ? (
                                        <iframe 
                                            title="CRIF Credit Report" 
                                            src={bureau.crifPdfUrl} 
                                            style={{ width: '100%', height: '600px', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#f8fafc' }} 
                                        />
                                    ) : (
                                        <div className="pg-empty" style={{ margin: 'auto' }}>
                                            <span className="pg-empty-icon">📊</span>
                                            <h3>No credit report available</h3>
                                            <p>Your credit bureau report has not been generated yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="pg-modal-footer">
                            <button className="pg-btn pg-btn-ghost" onClick={() => setViewType('')}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DocumentsPage;
